import React from "react";
import Api from "./service/Api";
import {MODE_DESKTOP, MODE_MOBILE, DEFAULT_ZOOM, DEFAULT_ZOOM_MOBILE} from "./Const";
import "./assets/style.scss";
import Desktop from "./views/Desktop";
import Mobile from "./views/Mobile";

const initialState = {
    application: {
        url: ''
    },
    layout: {
        mode: MODE_MOBILE,
        navigationOpen: false
    },
    mapProps: {
        center: {
            lat: 50.8835239,
            lng: 10.2,
        },
        zoom: DEFAULT_ZOOM,
        loading: false,
        height: 'calc(100vh - 83px)'
    },
    map: null,
    categories: [],
    members: [],
    membersCount: 0,
    membersMap: [],
    detail: {
        key: null,
        member: null
    },
    filter: {
        placeholder: 'Bitte Standort eingeben.',
        lat: null,
        lng: null,
        radius: 100,
        sort: null,
        categories: []
    }
}


// Define libraries as static constant to avoid re-rendering
const GOOGLE_MAPS_LIBRARIES = ['places'];

class App extends React.Component {

    static defaultProps = {
        libraries: GOOGLE_MAPS_LIBRARIES
    };

    constructor(props) {
        super(props);
        this.state = initialState;
        this.autocomplete = null;
        this.googlemap = null;
        this._isMounted = false;
        
        // Add global error handler
        window.addEventListener('error', this.handleGlobalError);
        window.addEventListener('resize', this.handleResize);
    }

    handleGlobalError = (error) => {
        console.error('Global error caught:', error);
        // Prevent the error from bubbling up and causing the generic "Script error"
        return true;
    }

    componentDidMount() {
        this._isMounted = true;
        this.setState({
            application: {
                ...this.state.application,
                url: window.location.href
            }
        });

        this.startUpApplication();
        this.handleResize();
        this.getCategories();

    }

    componentWillUnmount() {
        this._isMounted = false;
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('error', this.handleGlobalError);
    }

    startUpApplication = () => {

        let startupFilter = {...this.state.filter};

        try {

            const urlParams = new URLSearchParams(window.location.search);

            const categoryId = urlParams.get('categoryId');
            const categories = urlParams.get('categories');
            const lat = urlParams.get('lat');
            const lng = urlParams.get('lng');
            const radius = urlParams.get('radius');
            const sort = urlParams.get('sort');

            if (
                categoryId !== null &&
                categoryId !== '' &&
                !this.state.filter.categories.includes(categoryId)
            ) {
                startupFilter.categories.push(categoryId);
            } else if (categories !== null && categories !== '') {
                startupFilter.categories = categories.split(',');
            }

            if (lat !== null && lat !== '') {
                startupFilter.lat = lat;
            }

            if (lng !== null && lng !== '') {
                startupFilter.lng = lng;
            }

            if (radius !== null && radius !== '') {
                startupFilter.radius = parseInt(radius);
            }


            if (sort !== null && sort !== '') {
                startupFilter.sort = sort;
            }

            this.setState({
                filter: {
                    ...this.state.filter,
                    ...startupFilter
                }
            }, () => {
                this.getMembers(true);
            })

        } catch (error) {
            console.log(error);
        }

    }

    writeParameters = (filters) => {

        const urlParams = new URL(window.location);
        filters.forEach(filter => {
            let cond = filter.split('=');
            urlParams.searchParams.set(cond[0], cond[1]);
        })

        window.history.pushState({}, '', urlParams);

    }

    onUnmount = () => {
        this.setState({
            map: null
        })
    }



    handleResize = (resize) => {
        let mode = MODE_DESKTOP;
        if (window.innerWidth < 1024) {
            mode = MODE_MOBILE;
        }

        this.setState({
            mapProps: {
                ...this.state.mapProps,
                zoom: DEFAULT_ZOOM_MOBILE
            },
            layout: {
                ...this.state.layout,
                mode
            }
        })
    }

    handleMobileFilter = (event) => {
        event.preventDefault();
        this.setState({
            layout: {
                ...this.state.layout,
                navigationOpen: (!this.state.layout.navigationOpen)
            }
        })
    }

    getCategories = () => {
        Api.get('open/category?order=category_title&category_type=specialization').then(result => {
            if (this._isMounted) {
                this.setState({
                    categories: result.data?.items || []
                })
            }
        }).catch(error => {
            console.error('Error loading categories:', error);
            if (this._isMounted) {
                this.setState({
                    categories: []
                });
            }
        })
    }

    // Calculate distance between two coordinates using Haversine formula
    calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // Earth's radius in km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = 
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
    }

    // Client-side category filtering (same logic as Vue app)
    filterMembersByCategories = (members, selectedCategories) => {
        if (!selectedCategories || selectedCategories.length === 0) {
            return members;
        }

        return members.filter(member => {
            if (!member.member_category_ids) return false;

            const memberCategories = member.member_category_ids.split(',').map(cat => cat.trim());
            
            // Check if any selected category matches any of the member's categories
            return selectedCategories.some(selectedCat => 
                memberCategories.includes(selectedCat.toString())
            );
        });
    }

    // Client-side radius filtering with distance sorting
    filterMembersByRadius = (members, lat, lng, radius) => {
        const originLat = parseFloat(lat);
        const originLng = parseFloat(lng);
        const radiusValue = typeof radius === 'number' ? radius : parseFloat(radius);

        if (
            isNaN(originLat) ||
            isNaN(originLng) ||
            radiusValue === null ||
            radiusValue === undefined ||
            isNaN(radiusValue)
        ) {
            return members;
        }

        const membersWithDistance = members.map(member => {
            const memberLat = parseFloat(member.member_latitude);
            const memberLng = parseFloat(member.member_longitude);

            if (isNaN(memberLat) || isNaN(memberLng)) {
                return { ...member, distance: Infinity };
            }

            const distance = this.calculateDistance(originLat, originLng, memberLat, memberLng);
            return { ...member, distance: distance };
        });

        // Filter by radius and sort by distance
        return membersWithDistance
            .filter(member => member.distance <= radiusValue)
            .sort((a, b) => a.distance - b.distance);
    }

    applyFiltersWithRadius = (members, radius) => {
        let filteredMembers = members;
        const { lat, lng, categories } = this.state.filter;
        const normalizedRadius = typeof radius === 'number' ? radius : parseFloat(radius);
        const hasValidLocation =
            lat !== null && lat !== undefined && lat !== '' &&
            lng !== null && lng !== undefined && lng !== '' &&
            normalizedRadius !== null && normalizedRadius !== undefined && !isNaN(normalizedRadius);

        if (hasValidLocation) {
            filteredMembers = this.filterMembersByRadius(
                members,
                parseFloat(lat),
                parseFloat(lng),
                normalizedRadius
            );
        }

        return this.filterMembersByCategories(filteredMembers, categories);
    }

    getMembersWithAutoRadius = (members) => {
        const { lat, lng } = this.state.filter;
        const hasValidLocation =
            lat !== null && lat !== undefined && lat !== '' &&
            lng !== null && lng !== undefined && lng !== '';

        const radiusSteps = [100, 150, 200, 250, 300, 350, 400];
        const currentRadius = this.state.filter.radius ?? initialState.filter.radius;
        const normalizedRadius = typeof currentRadius === 'number'
            ? currentRadius
            : parseFloat(currentRadius) || initialState.filter.radius;

        if (!hasValidLocation) {
            return {
                members: this.filterMembersByCategories(members, this.state.filter.categories),
                radius: normalizedRadius
            };
        }

        const shouldAutoExpand = normalizedRadius === initialState.filter.radius;

        if (!shouldAutoExpand) {
            return {
                members: this.applyFiltersWithRadius(members, normalizedRadius),
                radius: normalizedRadius
            };
        }

        const steps = radiusSteps.includes(normalizedRadius)
            ? radiusSteps
            : [...radiusSteps, normalizedRadius].sort((a, b) => a - b);

        let startIndex = steps.findIndex(step => step >= normalizedRadius);
        if (startIndex === -1) {
            startIndex = steps.length - 1;
        }

        for (let i = startIndex; i < steps.length; i++) {
            const radius = steps[i];
            const filtered = this.applyFiltersWithRadius(members, radius);
            if (filtered.length > 0) {
                return {
                    members: filtered,
                    radius
                };
            }
        }

        const maxRadius = steps[steps.length - 1];
        return {
            members: this.applyFiltersWithRadius(members, maxRadius),
            radius: maxRadius
        };
    }

    getMembers = (firstLoad = false) => {

        if (this.state.loading === true) {
            return;
        }

        let filters = [];

        filters.push('member_post_status=publish')

        // Remove location and category filtering from API call - we'll filter client-side
        // if (this.state.filter.lat !== null) {
        //     filters.push('lat=' + this.state.filter.lat);
        // }

        // if (this.state.filter.lng !== null) {
        //     filters.push('lng=' + this.state.filter.lng);
        // }

        // if (this.state.filter.radius !== null) {
        //     filters.push('radius=' + this.state.filter.radius);
        // }

        // if (this.state.filter.categories.length > 0) {
        //     filters.push('categories=' + this.state.filter.categories.join(','));
        // }

        if (this.state.filter.sort !== null) {
            filters.push('sort=' + this.state.filter.sort);
        }

        filters.push('reducer=true');

        const apiUrl = 'open/member?' + filters.join('&');

        Api.get(apiUrl).then(result => {
            // Apply client-side filtering
            let allMembers = result.data?.items || [];
            let { lat, lng } = this.state.filter;
            const { categories } = this.state.filter;
            const layoutMode = this.state.layout.mode;

            let {
                members: filteredMembers,
                radius: effectiveRadius
            } = this.getMembersWithAutoRadius(allMembers);

            // Wenn Standort + Kategorie Filter aktiv sind aber keine Ergebnisse gefunden werden,
            // Standort-Filter entfernen und nur nach Kategorie filtern
            const hasLocationFilter = lat !== null && lat !== undefined && lat !== '' &&
                                      lng !== null && lng !== undefined && lng !== '';
            const hasCategoryFilter = categories && categories.length > 0;

            // Flag ob Standort-Filter entfernt werden soll
            let resetLocation = false;

            if (filteredMembers.length === 0 && hasLocationFilter && hasCategoryFilter) {
                // Nur nach Kategorie filtern (ohne Standort)
                filteredMembers = this.filterMembersByCategories(allMembers, categories);
                // Standort-Filter zurücksetzen
                lat = null;
                lng = null;
                effectiveRadius = 100;
                resetLocation = true;
            }

            if (this._isMounted) {
                this.setState({
                    filter: {
                        ...this.state.filter,
                        lat: lat,
                        lng: lng,
                        radius: effectiveRadius,
                        placeholder: resetLocation ? initialState.filter.placeholder : this.state.filter.placeholder
                    },
                    mapProps: {
                        ...this.state.mapProps,
                        zoom: this.getZoomLevel({
                            radius: effectiveRadius,
                            lat,
                            lng,
                            mode: layoutMode
                        }),
                        loading: false,
                        radius: effectiveRadius
                    },
                    members: filteredMembers,
                    membersCount: filteredMembers.length,
                }, () => {
                    if (this._isMounted) {
                        this.setState({
                            mapProps: {
                                ...this.state.mapProps,
                                zoom: this.getZoomLevel(),
                                center: this.getMapCenter(filteredMembers)
                            }
                        }, () => {
                            this.writeParameters(filters);
                        })
                    }
                })
            }

        }).catch(error => {
            console.error('Error loading members:', error);
            if (this._isMounted) {
                this.setState({
                    filter: {
                        ...this.state.filter,
                        radius: 100
                    },
                    mapProps: {
                        ...this.state.mapProps,
                        loading: false,
                        radius: 100
                    },
                    members: [],
                    membersCount: 0
                });
            }
        });


    }

    onLoadAutocomplete = (autocomplete) => {
        this.autocomplete = autocomplete
    }

    handleAutocomplete = (filter) => {
        this.setState({
            mapProps: {
                ...this.state.mapProps,
                center: {
                    lat: filter.lat,
                    lng: filter.lng,
                },
                zoom: 10 // Zoom in when location is selected
            },
            filter: {
                ...filter,
                radius: filter.radius || 100,
                sort: 'distance' // Auto-select distance sorting when location is set
            }
        }, () => {
            this.getMembers();
        })
    }

    removeAutocomplete = (event) => {
        event.preventDefault();
        this.setState({
            mapProps: initialState.mapProps,
            filter: {
                ...this.state.filter,
                lat: null,
                lng: null,
                radius: initialState.filter.radius
            }
        }, () => {
            // Clear URL parameters when removing location filter
            const url = new URL(window.location);
            url.searchParams.delete('lat');
            url.searchParams.delete('lng');
            url.searchParams.delete('radius');
            window.history.pushState({}, '', url);
            
            this.getMembers();
        })
    }

    handleToggleOpenInfoWindow = (event, member, memberIndex) => {

        this.setState({
            detail: {
                key: memberIndex,
                member: member
            }
        });

    };

    handleGeoPosition = (event) => {
        event.preventDefault();

        this.setState({
            filter: {
                ...this.state.filter,
                placeholder: 'Bitte warten...'
            }
        }, () => {
        })

        navigator.geolocation.getCurrentPosition((position) => {

            this.setState({
                mapProps: {
                    ...this.state.mapProps,
                    center: {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    }
                },
                filter: {
                    ...this.state.filter,
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    placeholder: 'Standort'
                }
            }, () => {
                this.getMembers();
            })
        });


    }

    handleCategorySelect = (event) => {
        let categories = [...this.state.filter.categories];

        if (event.target.checked === true) {
            categories.push(event.target.value);
        } else {
            const index = categories.indexOf(event.target.value);
            if (index > -1) {
                categories.splice(index, 1);
            }
        }

        this.setState({
            filter: {
                ...this.state.filter,
                categories: categories
            }
        }, () => {
            this.getMembers();
        })

    }


    search = (event) => {
        event.preventDefault();
    }

    getZoomLevel = ({ radius, lat, lng, mode } = {}) => {

        const layoutMode = mode || this.state.layout.mode;
        const targetLat = lat !== undefined ? lat : this.state.filter.lat;
        const targetLng = lng !== undefined ? lng : this.state.filter.lng;
        const targetRadius = radius !== undefined ? radius : this.state.filter.radius;

        try {
            if (
                targetLat === null || targetLat === undefined || targetLat === '' ||
                targetLng === null || targetLng === undefined || targetLng === ''
            ) {
                if (layoutMode === MODE_MOBILE) {
                    return DEFAULT_ZOOM_MOBILE;
                }
                return DEFAULT_ZOOM;
            }
        } catch (error) {

        }

        const radiusValue = typeof targetRadius === 'number'
            ? targetRadius
            : parseFloat(targetRadius) || 0;

        let zoom = DEFAULT_ZOOM;

        switch (true) {
            case (radiusValue < 50):
                zoom = 12;
                break;
            case (radiusValue > 300):
                zoom = 6;
                break;
            case (radiusValue > 200):
                zoom = 7;
                break;
            case (radiusValue >= 100):
                zoom = 8;
                break;
            case (radiusValue >= 50):
                zoom = 10;
                break;
            default:
                zoom = DEFAULT_ZOOM;
                if (layoutMode === MODE_MOBILE) {
                    zoom = DEFAULT_ZOOM_MOBILE;
                }

                break;
        }

        return zoom;
    }

    getMapCenter = (members) => {
        // If location filter is active, center on that location
        if (this.state.filter.lat && this.state.filter.lng) {
            return {
                lat: this.state.filter.lat,
                lng: this.state.filter.lng
            };
        }

        // If no location filter but we have members, fit to show all members
        if (members && members.length > 0) {
            const validMembers = members.filter(member => {
                const lat = parseFloat(member.member_latitude);
                const lng = parseFloat(member.member_longitude);
                return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
            });

            if (validMembers.length > 0) {
                // Calculate center of all member locations
                const avgLat = validMembers.reduce((sum, member) => 
                    sum + parseFloat(member.member_latitude), 0) / validMembers.length;
                const avgLng = validMembers.reduce((sum, member) => 
                    sum + parseFloat(member.member_longitude), 0) / validMembers.length;
                
                return {
                    lat: avgLat,
                    lng: avgLng
                };
            }
        }

        // Default center (Germany)
        return {
            lat: 50.8835239,
            lng: 10.2,
        };
    }

    handleSliderChange = (value) => {
        this.setState({
            filter: {
                ...this.state.filter,
                radius: value
            }
        })
    }

    handleSliderChangeComplete = () => {
        this.getMembers();
    }

    onChangeSort = (sort) => {
        this.setState({
            filter: {
                ...this.state.filter,
                sort: sort
            }
        }, () => {
            this.getMembers();
        })
    }

    render() {
        return (
            <div className="dg-application">
                {
                    (this.state.layout.mode === MODE_DESKTOP)
                    ? <Desktop
                            application={this.state.application}
                            filter={this.state.filter}
                            categories={this.state.categories}
                            members={this.state.members}
                            mapItems={this.state.membersMap}
                            mapProps={this.state.mapProps}
                            onSelectCategory={(event) => this.handleCategorySelect(event)}
                            onSelectPlace={this.handleAutocomplete}
                            onSliderChange={this.handleSliderChange}
                            onSliderChangeComplete={this.handleSliderChangeComplete}
                            onChangeSort={this.onChangeSort}
                            handleGeoPosition={this.handleGeoPosition}
                            removeGeoPosition={this.removeAutocomplete}
                            version={'DESKTOP'}

                        />
                    : <Mobile
                            application={this.state.application}
                            filter={this.state.filter}
                            categories={this.state.categories}
                            members={this.state.members}
                            mapItems={this.state.membersMap}
                            mapProps={this.state.mapProps}
                            onSelectCategory={(event) => this.handleCategorySelect(event)}
                            onSelectPlace={this.handleAutocomplete}
                            onSliderChange={this.handleSliderChange}
                            onSliderChangeComplete={this.handleSliderChangeComplete}
                            onChangeSort={this.onChangeSort}
                            handleGeoPosition={this.handleGeoPosition}
                            removeGeoPosition={this.removeAutocomplete}
                            version={'MOBILE'}
                        />
                }
            </div>
        )
    }

}

export default App;
